import { AssessmentSubtype, AssessmentType, SurveyStatus } from '@@types/api.types';
import { AssessmentLayout } from '@components/layouts/assessment.layout';
import { SurveyParametersSidebar } from '@components/pages/survey/components/survey-parameters-sidebar.component';
import { useIsMobile } from '@hooks/mobile.hook';
import { RETURN_TO_SEARCH_PARAM, getSafeReturnToPath } from '@hooks/return-to.hook';
import { useIsTablet } from '@hooks/tablet.hook';
import { useGetSurvey } from '@queries/survey.query';
import { useGetTechnology } from '@queries/technology.query';
import { useSurveyAttachmentsStore } from '@stores/survey-attachments.store';
import { useSurveySidebarStore } from '@stores/survey-sidebar.store';
import {
  resolveSurveySidebarBaseStateForSet,
  resolveSurveySidebarBaseStateForToggle,
  resolveSurveySidebarState,
  resolveSurveySidebarViewportMode,
} from '@stores/survey-sidebar.util';
import { useEffect } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { SurveySidebar } from '../pages/survey/components/survey-sidebar.component';

type SurveyLayoutProps = {
  children: React.ReactNode;
};

export const SurveyLayout: React.FC<SurveyLayoutProps> = (props) => {
  const { children } = props;

  const { surveyId, technologyId } = useParams();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const returnTo = getSafeReturnToPath({ returnTo: searchParams.get(RETURN_TO_SEARCH_PARAM) });
  const navigate = useNavigate();
  const { data: technology } = useGetTechnology(technologyId);
  const { data: survey } = useGetSurvey(surveyId);
  const isMobile = useIsMobile();
  const isTablet = useIsTablet();
  const viewportMode = resolveSurveySidebarViewportMode({ isMobile, isTablet });
  const clearSurveyBufferedAttachments = useSurveyAttachmentsStore((state) => state.clearSurveyBufferedAttachments);
  const { baseIsCollapsed, baseIsContextCollapsed, setBaseState } = useSurveySidebarStore();
  const { isCollapsed, isContextCollapsed } = resolveSurveySidebarState({
    baseIsCollapsed,
    baseIsContextCollapsed,
    viewportMode,
  });

  const surveyIsComplete = survey?.status === SurveyStatus.COMPLETE;
  const surveyIsDraft = survey?.status === SurveyStatus.DRAFT;

  const surveyStatus = surveyIsComplete ? 'Completed' : surveyIsDraft ? 'Draft' : '...';
  const returnToIsValid = Boolean(returnTo && !returnTo.includes('/survey/'));

  const handleExitSurvey = () => {
    if (returnToIsValid && returnTo) {
      navigate(returnTo);
      return;
    }

    const tab = survey?.assessmentSubtype === AssessmentSubtype.MILITARY ? 'ma' : survey?.assessmentType?.toLowerCase();
    navigate(`/technology/${technologyId}${tab ? `?tab=${tab}` : ''}`);
  };

  useEffect(() => {
    if (!surveyId) {
      return;
    }

    return () => {
      clearSurveyBufferedAttachments(surveyId);
    };
  }, [clearSurveyBufferedAttachments, surveyId]);

  const handleToggleCollapsed = (): void => {
    setBaseState(
      resolveSurveySidebarBaseStateForToggle({
        baseIsCollapsed,
        baseIsContextCollapsed,
        target: 'left',
        viewportMode,
      }),
    );
  };

  const handleToggleContextCollapsed = (): void => {
    setBaseState(
      resolveSurveySidebarBaseStateForToggle({
        baseIsCollapsed,
        baseIsContextCollapsed,
        target: 'right',
        viewportMode,
      }),
    );
  };

  const handleSetIsCollapsed = (collapsed: boolean): void => {
    setBaseState(
      resolveSurveySidebarBaseStateForSet({
        baseIsCollapsed,
        baseIsContextCollapsed,
        collapsed,
        target: 'left',
        viewportMode,
      }),
    );
  };

  return (
    <AssessmentLayout
      assessmentLabel={
        (survey?.assessmentType === AssessmentType.TRL && survey?.assessmentSubtype === AssessmentSubtype.MILITARY
          ? 'Military TRL'
          : undefined) ??
        (survey?.assessmentType === AssessmentType.TRL ? 'Rapid TRL' : undefined) ??
        (survey?.assessmentType === AssessmentType.MRL ? 'MRL' : '...')
      }
      leftSidebar={
        <SurveySidebar
          chapters={survey?.snapshot.chapters}
          isCollapsed={isCollapsed}
          setIsCollapsed={handleSetIsCollapsed}
          surveyIsComplete={surveyIsComplete}
          technologyId={technologyId}
        />
      }
      onExit={handleExitSurvey}
      onToggleLeft={handleToggleCollapsed}
      onToggleRight={survey && technology ? handleToggleContextCollapsed : undefined}
      rightSidebar={
        <SurveyParametersSidebar
          isCollapsed={isContextCollapsed}
          onToggleCollapsed={handleToggleContextCollapsed}
          survey={survey}
          technology={technology}
        />
      }
      rightSidebarAriaLabel={isContextCollapsed ? 'Expand TRL Parameters' : 'Collapse TRL Parameters'}
      statusLabel={surveyStatus}
      technologyName={technology?.name}
    >
      {children}
    </AssessmentLayout>
  );
};
